import { memo, useMemo } from "react";

import { ProductProvider, parseGid, useProduct } from "@shopify/hydrogen-react";
import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { type BadgeType } from "@components/Badge";
import { ProductCardPlaceholder } from "@components/ProductCard/ProductCardPlaceholder.view";
import { isColorOption } from "@react/utilities";
import { type OPProductConnection } from "@root/tablescape/state";
import { formatProduct, formatProductVariant } from "@tablescape/utilities";

import { ProductCardView } from "./ProductCard.view";

type props = {
  nextLabel: string;
  prevLabel: string;
};

export const ProductCardBase = ({ nextLabel, prevLabel }: props) => {
  const { product, selectedVariant } = useProduct();

  const formattedProduct = useMemo(() => formatProduct(product), [product]);

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const colorOption = useMemo(
    () => formattedProduct.options?.find((option) => isColorOption(option)),
    [formattedProduct.options]
  );

  const variants = useMemo(() => {
    return formattedProduct.variants.filter((variant) => !variant.hidden);
  }, [formattedProduct.variants]);

  const activeVariantUrl = useMemo(() => {
    return `${formattedProduct.onlineStoreUrl}?variant=${
      parseGid(selectedVariant.id).id
    }`;
  }, [selectedVariant, formattedProduct.onlineStoreUrl]);

  const resolvedBadgeType = useMemo<Maybe<BadgeType>>(() => {
    switch (formattedSelectedVariant.badgeType) {
      case "sage":
        return "tertiary";
      case "brick":
        return "quaternary";
      case "bright-blue":
        return "senary";
      default:
        return "primary";
    }
  }, [formattedSelectedVariant]);

  const truncatedLabels = useMemo(() => {
    return formattedProduct.labels?.slice(0, 3);
  }, [formattedProduct.labels]);

  if (!variants.length) {
    return null;
  }

  return product ? (
    <ProductCardView
      activeVariant={formattedSelectedVariant}
      activeVariantUrl={activeVariantUrl}
      badgeText={formattedSelectedVariant.badgeText}
      badgeType={resolvedBadgeType}
      colorOption={colorOption}
      compareAtPriceDisplay={formattedSelectedVariant.compareAtPriceDisplay}
      description={formattedProduct.description}
      labels={truncatedLabels}
      nextLabel={nextLabel}
      prevLabel={prevLabel}
      setSize={formattedProduct.setSize}
      title={formattedProduct.title}
    />
  ) : (
    <ProductCardPlaceholder />
  );
};

const ProductCardWithProvider = ({
  nextLabel,
  prevLabel,
  product,
}: {
  nextLabel: string;
  prevLabel: string;
  product: OPProductConnection;
}) => (
  <ProductProvider data={product}>
    <ProductCardBase nextLabel={nextLabel} prevLabel={prevLabel} />
  </ProductProvider>
);

export const ProductCard = memo(ProductCardWithProvider);
